(() => {
  const GOOGLE_SCRIPT_ATTR = "data-az-google-maps";
  const ZIP_SEARCH_RADIUS_MILES = 50;
  let googleMapsPromise;

  const toRadians = (value) => (value * Math.PI) / 180;

  const haversineDistanceMiles = (from, to) => {
    const earthRadiusMiles = 3958.8;
    const deltaLat = toRadians(to.lat - from.lat);
    const deltaLng = toRadians(to.lng - from.lng);
    const startLat = toRadians(from.lat);
    const endLat = toRadians(to.lat);

    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(startLat) * Math.cos(endLat) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return earthRadiusMiles * c;
  };

  const loadGoogleMaps = (apiKey) => {
    if (!apiKey) return Promise.reject(new Error("Missing Google Maps API key"));
    if (window.google && window.google.maps) return Promise.resolve(window.google.maps);
    if (googleMapsPromise) return googleMapsPromise;

    googleMapsPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`[${GOOGLE_SCRIPT_ATTR}]`);
      if (existingScript) {
        existingScript.addEventListener("load", () => resolve(window.google.maps), { once: true });
        existingScript.addEventListener("error", () => reject(new Error("Failed to load Google Maps")), { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
      script.async = true;
      script.defer = true;
      script.setAttribute(GOOGLE_SCRIPT_ATTR, "true");
      script.onload = () => {
        if (window.google && window.google.maps) {
          resolve(window.google.maps);
          return;
        }
        reject(new Error("Google Maps unavailable"));
      };
      script.onerror = () => reject(new Error("Failed to load Google Maps"));
      document.head.appendChild(script);
    });

    return googleMapsPromise;
  };

  const escapeHtml = (value) =>
    String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const parseNumber = (value, fallback) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  class StoreLocator {
    constructor(root) {
      this.root = root;
      this.resultsEl = root.querySelector("[data-az-results]");
      this.statusEl = root.querySelector("[data-az-store-status]");
      this.emptyEl = root.querySelector("[data-az-empty-state]");
      this.emptyTitleEl = root.querySelector("[data-az-empty-title]");
      this.emptyCopyEl = root.querySelector("[data-az-empty-copy]");
      this.emptyCtaEl = root.querySelector("[data-az-empty-cta]");
      this.searchInput = root.querySelector("[data-az-store-search]");
      this.geoButton = root.querySelector("[data-az-use-location]");
      this.mapToggle = root.querySelector("[data-az-map-toggle]");
      this.mapShell = root.querySelector("[data-az-map-shell]");
      this.mapEl = root.querySelector("[data-az-map]");
      this.mapPlaceholder = root.querySelector("[data-az-map-placeholder]");
      this.mapTitleEl = root.querySelector("[data-az-map-title]");
      this.mapTextEl = root.querySelector("[data-az-map-text]");

      this.config = {
        storesUrl: root.dataset.storesUrl,
        mapProvider: root.dataset.mapProvider || "none",
        googleMapsApiKey: root.dataset.googleMapsApiKey || "",
        mapboxAccessToken: root.dataset.mapboxAccessToken || "",
        defaultCenterLat: parseNumber(root.dataset.defaultCenterLat, 37.7749),
        defaultCenterLng: parseNumber(root.dataset.defaultCenterLng, -122.4194),
        defaultZoom: parseNumber(root.dataset.defaultZoom, 9)
      };

      this.state = {
        stores: [],
        filteredStores: [],
        activeStoreId: null,
        userCoords: null,
        query: "",
        mapReady: false,
        mapMode: "placeholder"
      };

      this.map = null;
      this.infoWindow = null;
      this.markers = new Map();
      this.geocodeCache = new Map();
      this.filterRequestId = 0;

      this.bindEvents();
      this.initialize();
    }

    bindEvents() {
      if (this.searchInput) {
        this.searchInput.addEventListener("input", () => {
          this.state.query = this.searchInput.value.trim();
          this.applyFilters();
        });
      }

      if (this.geoButton) {
        this.geoButton.addEventListener("click", () => {
          this.requestLocation();
        });
      }

      if (this.mapToggle) {
        this.mapToggle.addEventListener("click", () => {
          const expanded = this.mapToggle.getAttribute("aria-expanded") === "true";
          this.root.classList.toggle("is-map-open", !expanded);
          this.mapToggle.setAttribute("aria-expanded", String(!expanded));
          this.mapToggle.textContent = expanded ? "Show Map" : "Hide Map";
          this.scheduleMapResize();
        });
      }

      window.addEventListener("resize", () => this.scheduleMapResize(), { passive: true });
    }

    async initialize() {
      this.root.classList.add("is-initialized");
      this.setStatus("Loading stores...");
      this.renderLoading();

      try {
        const response = await fetch(this.config.storesUrl, { credentials: "same-origin" });
        if (!response.ok) {
          throw new Error(`Failed to load stores (${response.status})`);
        }

        const stores = await response.json();
        this.state.stores = Array.isArray(stores) ? stores.map((store, index) => this.normalizeStore(store, index)) : [];
        this.state.filteredStores = [...this.state.stores];
        await this.initializeMap();
        this.applyFilters();
      } catch (error) {
        this.state.stores = [];
        this.state.filteredStores = [];
        this.renderResults();
        this.setStatus("Unable to load stores right now.");
        this.showEmptyState("your search");
        this.updatePlaceholderCopy({
          title: "Store data unavailable.",
          text: "The locations file could not be loaded. Check the stores JSON asset path in section settings."
        });
      }
    }

    normalizeStore(store, index) {
      const lat = parseNumber(store.lat, null);
      const lng = parseNumber(store.lng, null);
      const tags = Array.isArray(store.tags) ? store.tags.filter(Boolean) : [];

      return {
        id: `store-${index + 1}`,
        name: store.name || "Store",
        address1: store.address1 || "",
        address2: store.address2 || "",
        phone: store.phone || "",
        city: store.city || "",
        state: store.state || "",
        zip: store.zip || "",
        country: store.country || "",
        lat,
        lng,
        tags,
        url: store.url || "",
        distance: null
      };
    }

    async initializeMap() {
      const canUseMapbox =
        this.config.mapProvider === "mapbox" &&
        this.config.mapboxAccessToken &&
        this.mapEl &&
        window.mapboxgl;

      if (canUseMapbox) {
        try {
          window.mapboxgl.accessToken = this.config.mapboxAccessToken;
          this.map = new window.mapboxgl.Map({
            container: this.mapEl,
            style: "mapbox://styles/mapbox/streets-v12",
            center: [this.config.defaultCenterLng, this.config.defaultCenterLat],
            zoom: this.config.defaultZoom,
            attributionControl: false
          });

          this.map.addControl(new window.mapboxgl.NavigationControl({ showCompass: false }), "top-right");
          this.map.on("load", () => this.scheduleMapResize());
          this.state.mapMode = "mapbox";
          this.state.mapReady = true;
          this.mapEl.hidden = false;
          this.mapPlaceholder.hidden = true;
          this.scheduleMapResize();
          return;
        } catch (error) {
          this.state.mapMode = "placeholder";
          this.mapEl.hidden = true;
          this.mapPlaceholder.hidden = false;
          this.updatePlaceholderCopy({
            title: "Map temporarily unavailable.",
            text: "The live Mapbox map could not be loaded. Results still work, and the selected store details will appear here."
          });
          return;
        }
      }

      const canUseGoogleMaps =
        this.config.mapProvider === "google" &&
        this.config.googleMapsApiKey &&
        this.mapEl;

      if (!canUseGoogleMaps) {
        this.state.mapMode = "placeholder";
        this.mapEl.hidden = true;
        this.mapPlaceholder.hidden = false;
        this.updatePlaceholderCopy({
          title: "Select a store to preview it here.",
          text:
            this.config.mapProvider === "none"
              ? "Store results are still available. Select a location from the list to view its details here."
              : this.config.mapProvider === "mapbox"
                ? "Store results are still available. The live map is temporarily unavailable right now."
                : !this.config.googleMapsApiKey
                  ? "Store results are still available. The live map is temporarily unavailable right now."
                  : "Store results are still available. The live map is temporarily unavailable right now."
        });
        return;
      }

      try {
        const maps = await loadGoogleMaps(this.config.googleMapsApiKey);
        this.map = new maps.Map(this.mapEl, {
          center: {
            lat: this.config.defaultCenterLat,
            lng: this.config.defaultCenterLng
          },
          zoom: this.config.defaultZoom,
          disableDefaultUI: true,
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          styles: [
            { featureType: "poi", stylers: [{ visibility: "off" }] },
            { featureType: "transit", stylers: [{ visibility: "off" }] },
            { featureType: "road", elementType: "geometry", stylers: [{ color: "#f3e8c1" }] },
            { featureType: "water", stylers: [{ color: "#efe7ca" }] },
            { featureType: "landscape", stylers: [{ color: "#fbf3d6" }] }
          ]
        });
        this.infoWindow = new maps.InfoWindow();
        this.state.mapMode = "google";
        this.state.mapReady = true;
        this.mapEl.hidden = false;
        this.mapPlaceholder.hidden = true;
        this.scheduleMapResize();
      } catch (error) {
        this.state.mapMode = "placeholder";
        this.mapEl.hidden = true;
        this.mapPlaceholder.hidden = false;
        this.updatePlaceholderCopy({
          title: "Map temporarily unavailable.",
          text: "The live map could not be loaded. Results still work, and the selected store details will appear here."
        });
      }
    }

    renderLoading() {
      if (!this.resultsEl) return;
      this.resultsEl.innerHTML = `
        <div class="az-store-locator__result az-store-locator__result--loading">
          <p class="az-store-locator__result-name">Loading store list...</p>
          <p class="az-store-locator__result-address">Pulling nearby locations now.</p>
        </div>
      `;
      this.hideEmptyState();
    }

    setStatus(message) {
      if (this.statusEl) {
        this.statusEl.textContent = message;
      }
    }

    requestLocation() {
      if (!navigator.geolocation) {
        this.setStatus("Geolocation is not supported on this device.");
        return;
      }

      this.setStatus("Finding your nearest stores...");
      this.geoButton.disabled = true;

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.state.userCoords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          this.geoButton.disabled = false;
          this.setStatus("Showing nearest stores first.");
          this.applyFilters();
        },
        () => {
          this.geoButton.disabled = false;
          this.setStatus("We could not access your location. Search by ZIP, city, or state.");
        },
        {
          enableHighAccuracy: false,
          maximumAge: 300000,
          timeout: 10000
        }
      );
    }

    async geocodeSearchQuery(query) {
      if (!this.config.mapboxAccessToken || !query) return null;
      if (this.geocodeCache.has(query)) return this.geocodeCache.get(query);

      try {
        const params = new URLSearchParams({
          access_token: this.config.mapboxAccessToken,
          country: "US",
          types: "postcode",
          limit: "1"
        });
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?${params.toString()}`
        );
        if (!response.ok) throw new Error(`Geocode failed (${response.status})`);

        const data = await response.json();
        const feature = Array.isArray(data.features) ? data.features[0] : null;
        const coords =
          feature && Array.isArray(feature.center) && feature.center.length >= 2
            ? { lng: Number(feature.center[0]), lat: Number(feature.center[1]) }
            : null;

        this.geocodeCache.set(query, coords);
        return coords;
      } catch (error) {
        return null;
      }
    }

    async applyFilters() {
      const requestId = ++this.filterRequestId;
      const query = (this.state.query || "").trim().toLowerCase();
      const zipQuery = query.match(/^\d{5}(?:-\d{4})?$/) ? query.slice(0, 5) : "";
      const zipCoords = zipQuery ? await this.geocodeSearchQuery(zipQuery) : null;

      if (requestId !== this.filterRequestId) return;

      let filtered = this.state.stores.filter((store) => {
        if (!query) return true;

        if (zipQuery && zipCoords) {
          return true;
        }

        const haystack = [
          store.name,
          store.address1,
          store.address2,
          store.phone,
          store.city,
          store.state,
          store.zip,
          ...store.tags
        ]
          .join(" ")
          .toLowerCase();

        return haystack.includes(query);
      });

      const distanceOrigin = zipCoords || this.state.userCoords;
      filtered = filtered.map((store) => ({
        ...store,
        distance:
          distanceOrigin && Number.isFinite(store.lat) && Number.isFinite(store.lng)
            ? haversineDistanceMiles(distanceOrigin, { lat: store.lat, lng: store.lng })
            : null
      }));

      if (zipQuery && zipCoords) {
        const nearbyStores = filtered.filter((store) => store.distance != null && store.distance <= ZIP_SEARCH_RADIUS_MILES);
        const closestStores = [...filtered].filter((store) => store.distance != null);

        closestStores.sort((a, b) => a.distance - b.distance);
        nearbyStores.sort((a, b) => a.distance - b.distance);

        if (nearbyStores.length) {
          filtered = nearbyStores;
          this.hideEmptyState();
        } else {
          filtered = closestStores;
          this.showEmptyState({
            title: "No nearby stores found for that ZIP code.",
            copy: "Showing the closest stores to you for now. You can also shop online any time.",
            showCta: true
          });
        }
      }

      filtered.sort((a, b) => {
        if (a.distance != null && b.distance != null) {
          return a.distance - b.distance;
        }
        if (a.distance != null) return -1;
        if (b.distance != null) return 1;
        return a.name.localeCompare(b.name);
      });

      this.state.filteredStores = filtered;

      if (!filtered.length) {
        this.state.activeStoreId = null;
        this.renderResults();
        this.showEmptyState({
          title: "No stores found near your search.",
          copy: "Try a nearby ZIP code, broader city search, or use your current location.",
          showCta: true
        });
        this.setStatus("No matching stores found.");
        this.updatePlaceholderCopy({
          title: "No matching stores yet.",
          text: `No stores found near ${this.state.query || "your search"}. Try a nearby ZIP code or use your current location.`
        });
        this.syncMapMarkers();
        return;
      }

      if (!(zipQuery && zipCoords && !filtered.some((store) => store.distance != null && store.distance <= ZIP_SEARCH_RADIUS_MILES))) {
        this.hideEmptyState();
      }
      if (!filtered.some((store) => store.id === this.state.activeStoreId)) {
        this.state.activeStoreId = filtered[0].id;
      }

      const withDistance = filtered.filter((store) => store.distance != null).length;
      if (zipQuery && zipCoords) {
        const nearbyCount = filtered.filter((store) => store.distance != null && store.distance <= ZIP_SEARCH_RADIUS_MILES).length;
        this.setStatus(
          nearbyCount
            ? `${nearbyCount} store${nearbyCount === 1 ? "" : "s"} found within ${ZIP_SEARCH_RADIUS_MILES} miles. Closest stores appear first.`
            : `No stores found within ${ZIP_SEARCH_RADIUS_MILES} miles. Showing the closest stores to you instead.`
        );
      } else {
        this.setStatus(
          withDistance
            ? `${filtered.length} store${filtered.length === 1 ? "" : "s"} found. Nearest locations appear first.`
            : `${filtered.length} store${filtered.length === 1 ? "" : "s"} found.`
        );
      }
      this.renderResults();
      this.syncMapMarkers();
      this.focusStore(this.state.activeStoreId, false);
    }

    renderResults() {
      if (!this.resultsEl) return;

      if (!this.state.filteredStores.length) {
        this.resultsEl.innerHTML = "";
        return;
      }

      this.resultsEl.innerHTML = this.state.filteredStores
        .map((store, index) => {
          const cityStateZip = [[store.city, store.state].filter(Boolean).join(", "), store.zip].filter(Boolean).join(" ");
          const distance = store.distance != null ? `${store.distance.toFixed(1)} mi` : "";
          const tags = store.tags
            .map((tag) => `<span class="az-store-locator__tag">${escapeHtml(tag)}</span>`)
            .join("");
          const resultClasses =
            store.id === this.state.activeStoreId
              ? "az-store-locator__result is-active"
              : "az-store-locator__result";

          return `
            <button class="${resultClasses}" type="button" data-store-id="${escapeHtml(store.id)}">
              <span class="az-store-locator__result-top">
                <span class="az-store-locator__result-index">${String(index + 1).padStart(2, "0")}</span>
                ${distance ? `<span class="az-store-locator__result-distance">${escapeHtml(distance)}</span>` : ""}
              </span>
              <span class="az-store-locator__result-head">
                <span class="az-store-locator__result-name">${escapeHtml(store.name)}</span>
              </span>
              <span class="az-store-locator__result-address">
                <span>${escapeHtml(store.address1)}</span>
                ${store.address2 ? `<span>${escapeHtml(store.address2)}</span>` : ""}
                ${cityStateZip ? `<span>${escapeHtml(cityStateZip)}</span>` : ""}
              </span>
              ${store.phone ? `<span class="az-store-locator__result-phone">${escapeHtml(store.phone)}</span>` : ""}
              ${tags ? `<span class="az-store-locator__tags">${tags}</span>` : ""}
            </button>
          `;
        })
        .join("");

      this.resultsEl.querySelectorAll("[data-store-id]").forEach((button) => {
        button.addEventListener("click", () => {
          this.focusStore(button.getAttribute("data-store-id"), true);
        });
      });
    }

    syncMapMarkers() {
      if (this.state.mapMode === "mapbox" && this.map && window.mapboxgl) {
        this.markers.forEach((entry) => {
          if (entry && entry.marker) entry.marker.remove();
        });
        this.markers.clear();

        this.state.filteredStores.forEach((store) => {
          if (!Number.isFinite(store.lat) || !Number.isFinite(store.lng)) return;

          const popup = new window.mapboxgl.Popup({
            closeButton: true,
            closeOnClick: false,
            offset: 18
          }).setHTML(this.getPopupMarkup(store));
          popup.__azPinned = false;
          popup.on("close", () => {
            popup.__azPinned = false;
          });

          const markerElement = document.createElement("button");
          markerElement.type = "button";
          markerElement.className = "az-store-locator__marker";
          markerElement.setAttribute("aria-label", store.name);
          markerElement.innerHTML = `
            <span class="az-store-locator__marker-core" aria-hidden="true">
              <span class="az-store-locator__marker-dot"></span>
            </span>
          `;

          const marker = new window.mapboxgl.Marker({ element: markerElement, anchor: "bottom" })
            .setLngLat([store.lng, store.lat])
            .setPopup(popup)
            .addTo(this.map);

          markerElement.addEventListener("mouseenter", () => {
            if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
              this.openMapboxPopup(store.id);
            }
          });

          markerElement.addEventListener("mouseleave", () => {
            if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
              this.closeMapboxPopup(store.id);
            }
          });

          markerElement.addEventListener("focus", () => {
            this.openMapboxPopup(store.id);
          });

          markerElement.addEventListener("blur", () => {
            this.closeMapboxPopup(store.id);
          });

          markerElement.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.focusStore(store.id, true);
          });

          this.markers.set(store.id, { marker, popup });
        });

        this.updateMapboxMarkerState();
        return;
      }

      if (this.state.mapMode !== "google" || !this.map || !window.google || !window.google.maps) {
        this.markers.clear();
        return;
      }

      this.markers.forEach((marker) => marker.setMap(null));
      this.markers.clear();

      this.state.filteredStores.forEach((store) => {
        if (!Number.isFinite(store.lat) || !Number.isFinite(store.lng)) return;

        const marker = new window.google.maps.Marker({
          map: this.map,
          position: { lat: store.lat, lng: store.lng },
          title: store.name
        });

        marker.addListener("click", () => {
          this.focusStore(store.id, true);
        });

        this.markers.set(store.id, marker);
      });
    }

    getPopupMarkup(store) {
      const cityStateZip = [[store.city, store.state].filter(Boolean).join(", "), store.zip].filter(Boolean).join(" ");
      const tags = store.tags
        .map((tag) => `<span class="az-store-locator__tag">${escapeHtml(tag)}</span>`)
        .join("");

      return `
        <div class="az-store-locator__popup">
          <strong class="az-store-locator__popup-title">${escapeHtml(store.name)}</strong>
          <span class="az-store-locator__popup-line">${escapeHtml(store.address1)}</span>
          ${store.address2 ? `<span class="az-store-locator__popup-line">${escapeHtml(store.address2)}</span>` : ""}
          ${cityStateZip ? `<span class="az-store-locator__popup-line">${escapeHtml(cityStateZip)}</span>` : ""}
          ${store.phone ? `<span class="az-store-locator__popup-line az-store-locator__popup-phone">${escapeHtml(store.phone)}</span>` : ""}
          ${tags ? `<span class="az-store-locator__tags az-store-locator__popup-tags">${tags}</span>` : ""}
        </div>
      `;
    }

    scheduleMapResize() {
      if (!this.map || !this.state.mapReady) return;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.state.mapMode === "mapbox" && typeof this.map.resize === "function") {
            this.map.resize();
            return;
          }

          if (this.state.mapMode === "google" && window.google && window.google.maps) {
            window.google.maps.event.trigger(this.map, "resize");
          }
        });
      });
    }

    syncActiveResultCard(shouldScroll = false) {
      if (!this.resultsEl || !this.state.activeStoreId) return;

      const activeCard = this.resultsEl.querySelector(`[data-store-id="${this.state.activeStoreId}"]`);
      if (!activeCard) return;

      activeCard.setAttribute("aria-current", "true");

      if (shouldScroll) {
        activeCard.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
          block: "nearest"
        });
      }
    }

    updateMapboxMarkerState() {
      if (this.state.mapMode !== "mapbox") return;

      this.markers.forEach((entry, storeId) => {
        const markerElement = entry && entry.marker && typeof entry.marker.getElement === "function"
          ? entry.marker.getElement()
          : null;
        if (!markerElement) return;

        markerElement.classList.toggle("is-active", storeId === this.state.activeStoreId);
      });
    }

    openMapboxPopup(storeId) {
      if (this.state.mapMode !== "mapbox" || !this.map || !this.markers.has(storeId)) return;

      const entry = this.markers.get(storeId);
      const store = this.state.filteredStores.find((item) => item.id === storeId);
      if (!entry || !entry.popup || !entry.marker || !store) return;

      this.markers.forEach((item, itemStoreId) => {
        if (itemStoreId !== storeId && item && item.popup && item.popup.isOpen() && itemStoreId !== this.state.activeStoreId) {
          item.popup.remove();
        }
      });

      if (!entry.popup.isOpen()) {
        entry.popup.__azPinned = false;
        entry.popup.setHTML(this.getPopupMarkup(store));
        entry.marker.togglePopup();
      }
    }

    closeMapboxPopup(storeId) {
      if (this.state.mapMode !== "mapbox" || !this.markers.has(storeId)) return;

      const entry = this.markers.get(storeId);
      if (entry && entry.popup && entry.popup.isOpen() && !entry.popup.__azPinned) {
        entry.popup.remove();
      }
    }

    focusStore(storeId, shouldPanMap) {
      const store = this.state.filteredStores.find((item) => item.id === storeId);
      if (!store) return;

      this.state.activeStoreId = store.id;
      this.renderResults();
      this.syncActiveResultCard(shouldPanMap);

      const address = [store.address1, store.address2, store.city, store.state, store.zip].filter(Boolean).join(", ");
      const distanceText =
        store.distance != null ? `Approximately ${store.distance.toFixed(1)} miles away.` : "Distance available when location access is enabled.";

      this.updatePlaceholderCopy({
        title: store.name,
        text: `${address}. ${distanceText}`
      });

      if (this.state.mapMode === "mapbox" && this.map && this.markers.has(store.id)) {
        const entry = this.markers.get(store.id);

        this.markers.forEach((item, itemStoreId) => {
          if (!item || !item.popup || !item.popup.isOpen()) return;

          if (!shouldPanMap || itemStoreId !== store.id) {
            item.popup.__azPinned = false;
            item.popup.remove();
          }
        });

        if (shouldPanMap) {
          this.map.flyTo({
            center: [store.lng, store.lat],
            zoom: Math.max(this.config.defaultZoom, 11),
            essential: true
          });

          if (entry && entry.popup) {
            entry.popup.__azPinned = true;
            entry.popup.setHTML(this.getPopupMarkup(store));
            if (!entry.popup.isOpen()) {
              entry.marker.togglePopup();
            }
          }
        } else if (entry && entry.popup) {
          entry.popup.__azPinned = false;
        }

        this.updateMapboxMarkerState();
        this.syncActiveResultCard(shouldPanMap);
        return;
      }

      if (this.state.mapMode === "google" && this.map && this.markers.has(store.id)) {
        const marker = this.markers.get(store.id);
        if (shouldPanMap) {
          this.map.panTo(marker.getPosition());
          this.map.setZoom(Math.max(this.config.defaultZoom, 11));
        }
        if (this.infoWindow) {
          const popupLines = [
            `<strong>${escapeHtml(store.name)}</strong>`,
            escapeHtml(store.address1),
            store.address2 ? escapeHtml(store.address2) : "",
            escapeHtml([[store.city, store.state].filter(Boolean).join(", "), store.zip].filter(Boolean).join(" ")),
            store.phone ? escapeHtml(store.phone) : "",
            store.tags.length ? escapeHtml(store.tags.join(" | ")) : ""
          ].filter(Boolean);
          this.infoWindow.setContent(popupLines.join("<br>"));
          this.infoWindow.open({ anchor: marker, map: this.map });
        }
        this.syncActiveResultCard(shouldPanMap);
      }
    }

    updatePlaceholderCopy({ title, text }) {
      if (this.mapTitleEl) this.mapTitleEl.textContent = title;
      if (this.mapTextEl) this.mapTextEl.textContent = text;
    }

    showEmptyState({ title, copy, showCta = false }) {
      if (this.emptyEl) {
        this.emptyEl.hidden = false;
      }
      if (this.emptyTitleEl && title) {
        this.emptyTitleEl.textContent = title;
      }
      if (this.emptyCopyEl && copy) {
        this.emptyCopyEl.textContent = copy;
      }
      if (this.emptyCtaEl) {
        this.emptyCtaEl.hidden = !showCta;
      }
    }

    hideEmptyState() {
      if (this.emptyEl) {
        this.emptyEl.hidden = true;
      }
      if (this.emptyCtaEl) {
        this.emptyCtaEl.hidden = true;
      }
    }
  }

  document.querySelectorAll("[data-az-store-locator]").forEach((root) => {
    new StoreLocator(root);
  });
})();
